import React from 'react';
import { Navigate } from 'react-router-dom';
import { Row, Col, Button, Modal, OverlayTrigger, Popover } from 'react-bootstrap';
import DataTable from 'react-data-table-component';
import moment from 'moment';
import { canAccessPlanningModule } from '../../utils/roleConstants';
import config from '../../../config';
import utils from '../../utils';
import { canCreatePlanningPurchaseOrder } from './planningPurchaseOrderPermissions';
import DatacoLoadingIndicator from '../../_components/DatacoLoadingIndicator';

function toNumber(value) {
    const numberValue = Number(value);
    return Number.isFinite(numberValue) ? numberValue : 0;
}

function firstNumber(source, keys) {
    for (const key of keys) {
        if (source && source[key] !== undefined && source[key] !== null && source[key] !== '') {
            return toNumber(source[key]);
        }
    }
    return null;
}

function getKpiUnitPrice(row) {
    return toNumber(
        row?.unitPrice
        ?? row?.price
        ?? row?.purchasePrice
        ?? row?.lastPurchasePrice
        ?? row?.cost
        ?? row?.standardCost
    );
}

const PLANNING_PRICE_REFRESH_FLAG = "refreshPriceFromInventory";

function sumRowValue(rows, quantitySelector) {
    return (rows || []).reduce((sum, row) => sum + (toNumber(quantitySelector(row)) * getKpiUnitPrice(row)), 0);
}

function sumFgValue(fgDemand) {
    return (fgDemand || []).reduce((sum, row) => {
        const qty = firstNumber(row, ['qty', 'orderQty', 'demandQty', 'salesOrderQty', 'quantity']) || 0;
        return sum + (qty * getKpiUnitPrice(row));
    }, 0);
}

function buildMrpKpiMetrics({ summary = {}, bomList = [], shortages = [], fgDemand = [] }) {
    const requiredRows = (shortages || []).length > 0
        ? shortages
        : (bomList || []).filter(row => toNumber(row.availableQty) < 0);

    const onHoldValue = firstNumber(summary, [
        'onHoldInventoryValue',
        'totalOnHoldInventoryValue',
        'totalOnHoldValue',
        'totalOnHoldAmount',
    ]);
    const netInventoryValue = firstNumber(summary, [
        'netInventoryValue',
        'totalNetInventoryValue',
        'totalGoodInventoryValue',
    ]);
    const fgValue = firstNumber(summary, [
        'fgValue',
        'totalFgValue',
        'finishedGoodsValue',
        'totalFinishedGoodsValue',
    ]);
    const requiredToPurchaseValue = firstNumber(summary, [
        'requiredToPurchaseValue',
        'totalRequiredToPurchaseValue',
        'totalRequiredPurchaseValue',
        'purchaseRequiredValue',
        'totalShortageValue',
    ]);

    return [
        {
            label: 'Opened JO',
            value: firstNumber(summary, ['openedJoCount', 'openJoCount', 'openJobOrderCount', 'jobOrderCount']) || 0,
            tone: 'cyan',
            helper: 'Open job orders included in material reservations',
        },
        {
            label: 'Approved SO',
            value: firstNumber(summary, ['approvedSoCount', 'approvedSOCount', 'approvedSalesOrderCount', 'salesOrderCount']) || 0,
            tone: 'blue',
            helper: 'Approved sales orders included in demand',
        },
        {
            label: 'On Hold Inventory $',
            value: onHoldValue ?? sumRowValue(bomList, row => row.onHold),
            tone: 'amber',
            helper: 'On-hold inventory value',
        },
        {
            label: 'Net Inventory $',
            value: netInventoryValue ?? sumRowValue(bomList, row => (
                row.totalGoodInventory ?? (toNumber(row.goodParts) - toNumber(row.onHold) - toNumber(row.bookedQty))
            )),
            tone: 'green',
            helper: 'Good Parts minus On Hold and Booked Qty, valued by unit price',
        },
        {
            label: 'FG $',
            value: fgValue ?? sumFgValue(fgDemand),
            tone: 'slate',
            helper: 'Finished goods demand value',
        },
        {
            label: 'Required to Purchase $',
            value: requiredToPurchaseValue ?? sumRowValue(requiredRows, row => Math.abs(toNumber(row.availableQty))),
            tone: 'red',
            helper: 'Negative variance value requiring purchase',
        },
    ];
}

class MRP extends React.Component {
    headerLabel = (label, tooltip) => (
        <span
            title={tooltip}
            aria-label={`${label}: ${tooltip}`}
            style={{
                cursor: 'help',
                borderBottom: '1px dotted #6c757d',
                lineHeight: '1.4',
                display: 'inline-block',
            }}
        >
            {label}
        </span>
    );

    toggleCompactList = (key) => {
        this.setState(prev => ({
            compactListVersion: prev.compactListVersion + 1,
            expandedCompactLists: {
                ...prev.expandedCompactLists,
                [key]: !prev.expandedCompactLists[key],
            },
        }));
    };

    renderCompactList = (values, label, maxVisible = 2, key) => {
        const list = [...new Set((values || []).filter(Boolean).map(value => String(value).trim()).filter(Boolean))];
        if (list.length === 0) return <span style={{ color: '#6c757d' }}>-</span>;

        const expanded = !!this.state.expandedCompactLists[key];
        const visible = expanded ? list : list.slice(0, maxVisible);
        const hiddenCount = list.length - visible.length;
        const listLabel = `${list.length} ${label}${list.length === 1 ? '' : 's'}`;
        const title = list.join('\n');

        const chipStyle = {
            display: 'inline-block',
            maxWidth: '108px',
            padding: '2px 7px',
            borderRadius: '4px',
            backgroundColor: '#eef2f7',
            color: '#263238',
            fontSize: '12px',
            fontWeight: 600,
            lineHeight: '16px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            verticalAlign: 'middle',
        };
        const actionStyle = {
            ...chipStyle,
            maxWidth: '82px',
            border: '1px solid #ced4da',
            backgroundColor: '#fff',
            color: '#0d6efd',
            cursor: 'pointer',
        };

        return (
            <div title={title} style={{ width: '100%', padding: '4px 0' }}>
                <div style={{ fontSize: '11px', color: '#6c757d', fontWeight: 600, marginBottom: '4px' }}>
                    {listLabel}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap' }}>
                    {visible.map(item => (
                        <span key={item} style={chipStyle}>{item}</span>
                    ))}
                    {hiddenCount > 0 && (
                        <button
                            type="button"
                            style={actionStyle}
                            onMouseDown={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                            }}
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                this.toggleCompactList(key);
                            }}
                            aria-label={`Show all ${listLabel}`}
                        >
                            +{hiddenCount} more
                        </button>
                    )}
                    {expanded && list.length > maxVisible && (
                        <button
                            type="button"
                            style={actionStyle}
                            onMouseDown={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                            }}
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                this.toggleCompactList(key);
                            }}
                            aria-label={`Collapse ${listLabel}`}
                        >
                            show less
                        </button>
                    )}
                </div>
            </div>
        );
    };

    constructor(props) {
        super(props);
        this.state = {
            bomList: [],
            shortages: [],
            fgDemand: [],
            salesOrders: [],
            summary: {},
            uoms: [],
            sources: [],
            loading: false,
            searched: false,
            error: null,
            purchaseOrderDraft: null,
            expandedCompactLists: {},
            compactListVersion: 0,
            goodPartsBreakdownModal: null,
            bookedBreakdownModal: null,
        };

        this.columns = [
            {
                name: 'No',
                selector: (row, i) => i + 1,
                cell: (row, i) => i + 1,
                width: '60px',
            },
            {
                name: 'Material',
                selector: row => row.material,
                sortable: true,
                minWidth: '130px',
                wrap: true,
            },
            {
                name: 'Material Description',
                selector: row => row.materialDesc,
                sortable: true,
                minWidth: '180px',
                grow: 2,
                wrap: true,
            },
            {
                name: 'FG Parts',
                selector: row => (row.fgParts || []).join(', '),
                cell: row => this.renderCompactList(row.fgParts, 'FG part', 2, `${row.material || row.id}-fg-parts`),
                sortable: true,
                minWidth: '190px',
                grow: 1,
            },
            {
                name: 'SO Ref',
                selector: row => (row.soRefs || []).join(', '),
                cell: row => this.renderCompactList(row.soRefs, 'SO ref', 2, `${row.material || row.id}-so-refs`),
                sortable: true,
                minWidth: '170px',
                grow: 1,
            },
            {
                name: 'UoM',
                selector: row => row.uom,
                sortable: true,
                width: '85px',
            },
            {
                name: 'Where Use',
                selector: row => row.whereUse,
                sortable: true,
                width: '130px',
            },
            {
                name: 'Source',
                selector: row => row.source,
                sortable: true,
                width: '95px',
            },
            {
                name: 'Supplier',
                selector: row => row.supplier,
                sortable: true,
                minWidth: '130px',
                wrap: true,
            },
            {
                name: this.headerLabel('Good Parts', 'Accepted raw material inventory.'),
                selector: row => row.goodParts,
                sortable: true,
                width: '115px',
                right: true,
                cell: row => this.renderGoodPartsCell(row),
            },
            {
                name: this.headerLabel('On Hold', 'Material quantity not available for use.'),
                selector: row => row.onHold,
                sortable: true,
                width: '110px',
                right: true,
            },
            {
                name: this.headerLabel('Booked Qty', 'Material reserved for planned production jobs.'),
                selector: row => row.bookedQty,
                sortable: true,
                width: '115px',
                right: true,
                cell: row => this.renderBookedQtyCell(row),
            },
            {
                name: this.headerLabel('Net Inventory', 'Good Parts - On Hold - Booked Qty.'),
                selector: row => row.totalGoodInventory,
                sortable: true,
                width: '130px',
                right: true,
            },
            {
                name: this.headerLabel('Available Quantity', 'Net inventory after approved Sales Order demand.'),
                selector: row => row.availableQty,
                sortable: true,
                width: '150px',
                cell: row => {
                    const qty = row.availableQty;
                    const color = qty > 0 ? '#28a745' : qty < 0 ? '#dc3545' : '#6c757d';
                    return (
                        <span style={{ color, fontWeight: '600' }}>
                            {Number(qty || 0).toLocaleString()}
                        </span>
                    );
                },
            },
        ];

        this.summaryColumns = [
            {
                name: 'Material',
                selector: row => row.material,
                sortable: true,
                width: '140px',
            },
            {
                name: 'Material Description',
                selector: row => row.materialDesc,
                sortable: true,
                minWidth: '200px',
                grow: 2,
                wrap: true,
            },
            {
                name: 'Shortage Qty',
                selector: row => Math.abs(Number(row.availableQty) || 0),
                sortable: true,
                width: '130px',
                right: true,
                cell: row => (
                    <div style={{
                        backgroundColor: '#dc3545',
                        color: '#fff',
                        fontWeight: '700',
                        padding: '2px 10px',
                        borderRadius: '4px',
                        minWidth: '60px',
                        textAlign: 'center'
                    }}>
                        {Math.abs(Number(row.availableQty) || 0).toLocaleString()}
                    </div>
                ),
            },
            {
                name: 'Supplier',
                selector: row => row.supplier,
                sortable: true,
                minWidth: '150px',
                wrap: true,
            },
            {
                name: 'SO Ref',
                selector: row => (row.soRefs || []).join(', '),
                cell: row => this.renderCompactList(row.soRefs, 'SO ref', 3, `${row.material || row.id}-summary-so-refs`),
                sortable: true,
                minWidth: '190px',
            },
            {
                name: 'Action',
                width: '130px',
                cell: row => (
                    <Button
                        variant="outline-primary"
                        size="sm"
                        disabled={!this.canCreatePurchaseOrder()}
                        title={this.canCreatePurchaseOrder() ? 'Create Purchase Order' : 'Accounting role required'}
                        onClick={() => this.handleCreatePurchaseOrder(row)}
                    >
                        Create PO
                    </Button>
                ),
                ignoreRowClick: true,
                allowOverflow: true,
                button: true,
            },
        ];
    }

    getCurrentUser = () => {
        try {
            return JSON.parse(localStorage.getItem('user') || '{}');
        } catch (e) {
            return null;
        }
    };

    canCreatePurchaseOrder = () => canCreatePlanningPurchaseOrder(this.getCurrentUser());

    componentDidMount() {
        this.loadLookups().then(() => this.loadMrp());
    }

    fetchJson = (url) => {
        return fetch(url, { method: 'GET', headers: { 'Content-Type': 'application/json' } })
            .then(r => r.text())
            .then(text => (text ? JSON.parse(text) : []));
    };

    loadLookups = () => {
        return Promise.all([
            this.fetchJson(`${config.apiUrl}/getAllMaterialUom`),
            this.fetchJson(`${config.apiUrl}/getAllMaterialSource`)
        ]).then(([uoms, sources]) => {
            this.setState({ uoms, sources });
        }).catch(() => {
            this.setState({ uoms: [], sources: [] });
        });
    };

    loadMrp = () => {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        if (!user || !user.accountId) return;

        this.setState({ loading: true, error: null, searched: false });

        const url = new URL(`${config.apiUrl}/getMaterialRequirementsPlanning`);
        url.searchParams.append('company', user.accountId);
        url.searchParams.append('account_id', user.id || user.accountId);

        fetch(url.toString())
            .then(r => {
                if (!r.ok) throw new Error(`Server returned ${r.status}`);
                return r.json();
            })
            .then(data => {
                const bomList = (data.bomList || []).map(item => ({
                    ...item,
                    uom: utils.mapTo(this.state.uoms, item.uom),
                    source: utils.mapTo(this.state.sources, item.source),
                }));

                const shortages = (data.shortages || [])
                    .map(item => ({
                        ...item,
                        uom: utils.mapTo(this.state.uoms, item.uom),
                        source: utils.mapTo(this.state.sources, item.source),
                    }));

                this.setState({
                    bomList,
                    shortages,
                    fgDemand: data.fgDemand || [],
                    salesOrders: data.salesOrders || [],
                    summary: data.summary || {},
                    loading: false,
                    searched: true,
                });
            })
            .catch(err => {
                console.error('MRP fetch error:', err);
                this.setState({
                    loading: false,
                    searched: true,
                    error: err.message || 'Failed to fetch MRP data.',
                    bomList: [],
                    shortages: [],
                });
            });
    };

    getRowUnitPrice = (row) => {
        const price = row?.unitPrice ?? row?.price ?? row?.purchasePrice ?? row?.lastPurchasePrice ?? row?.cost ?? row?.standardCost ?? 0;
        const numberPrice = Number(price);
        return Number.isFinite(numberPrice) ? numberPrice : 0;
    };

    handleCreatePurchaseOrder = (row) => {
        if (!this.canCreatePurchaseOrder()) {
            alert('Accounting role is required to create a Purchase Order.');
            return;
        }

        const shortageQty = Math.abs(Number(row.availableQty) || 0);
        const deliveryDate = moment().format('YYYY-MM-DD');
        const draft = {
            date: deliveryDate,
            deliveryDate,
            supplier: row.supplier || '',
            shippingAddress: '',
            currency: row.currency || 'MYR',
            status: 'Draft',
            notes: [
                'Created from MRP negative variance.',
                `Material: ${row.material || '-'}.`,
                `SO Ref: ${(row.soRefs || []).join(', ') || '-'}.`,
                `FG Parts: ${(row.fgParts || []).join(', ') || '-'}.`,
                `Available Qty: ${row.availableQty ?? 0}.`,
            ].join('\n'),
            items: [{
                itemCode: row.material || '',
                description: row.materialDesc || '',
                unit: row.uom || '',
                qty: shortageQty || 1,
                unitPrice: this.getRowUnitPrice(row),
                [PLANNING_PRICE_REFRESH_FLAG]: true,
                selected: true,
                restock: false,
            }],
        };

        try {
            sessionStorage.setItem('mrpPurchaseOrderDraft', JSON.stringify(draft));
        } catch (e) {
            // Navigation state below is still enough for normal in-app routing.
        }

        this.setState({ purchaseOrderDraft: draft });
    };

    formatSummaryNumber = (value) => {
        return Number(value || 0).toLocaleString();
    };

    formatDetailNumber = (value) => {
        const numberValue = Number(value || 0);
        return Number.isFinite(numberValue)
            ? numberValue.toLocaleString(undefined, { maximumFractionDigits: 4 })
            : value;
    };

    renderBookedQtyCell = (row) => {
        const bookedQty = Number(row.bookedQty || 0);
        const breakdownCount = (row.bookedBreakdown || []).length;
        if (bookedQty === 0 && breakdownCount === 0) return '0';

        return (
            <button
                type="button"
                onClick={(e) => {
                    e.stopPropagation();
                    this.setState({ bookedBreakdownModal: row });
                }}
                title={breakdownCount > 0 ? 'View JO and SO breakdown' : 'No breakdown available'}
                style={{
                    border: 0,
                    background: 'transparent',
                    padding: 0,
                    textDecoration: 'underline',
                    fontWeight: 600,
                    cursor: 'pointer',
                }}
            >
                {this.formatDetailNumber(bookedQty)}
            </button>
        );
    };

    renderGoodPartsCell = (row) => {
        const goodParts = Number(row.goodParts || 0);
        const breakdownCount = (row.goodPartsBreakdown || []).length;
        if (goodParts === 0 && breakdownCount === 0) return '0';

        return (
            <button
                type="button"
                onClick={(e) => {
                    e.stopPropagation();
                    this.setState({ goodPartsBreakdownModal: row });
                }}
                title={breakdownCount > 0 ? 'View inventory movement breakdown' : 'No breakdown available'}
                style={{
                    border: 0,
                    background: 'transparent',
                    padding: 0,
                    textDecoration: 'underline',
                    fontWeight: 600,
                    cursor: 'pointer',
                }}
            >
                {this.formatDetailNumber(goodParts)}
            </button>
        );
    };

    formatBreakdownDate = (value) => {
        if (!value) return '-';
        const parsed = moment(value);
        return parsed.isValid() ? parsed.format('YYYY-MM-DD HH:mm') : value;
    };

    formatMovementLocation = (item) => {
        if (item.sourceType === 'BALANCE') {
            return item.area ? `${item.locationCode || '-'} (${item.area})` : (item.locationCode || '-');
        }
        const from = item.fromLocationCode || '-';
        const to = item.toLocationCode || item.locationCode || '-';
        return `${from} -> ${to}`;
    };

    renderMovementQuantity = (item) => {
        const rawValue = item.signedQty ?? item.quantity ?? 0;
        const qty = Number(rawValue || 0);
        const color = qty < 0 ? '#dc3545' : qty > 0 ? '#198754' : '#495057';
        const prefix = qty > 0 && item.direction !== 'BALANCE' ? '+' : '';

        return (
            <span style={{ color, fontWeight: 700 }}>
                {prefix}{this.formatDetailNumber(qty)}
            </span>
        );
    };

    renderBreakdownSummaryCard = ({ label, value, helper, color }) => {
        const accent = color || '#212529';
        const helperId = `mrp-breakdown-${label.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`;

        return (
            <div style={{ padding: '8px 10px', border: '1px solid #dee2e6', borderRadius: '6px', minWidth: '150px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                    <div style={{ fontSize: '11px', fontWeight: 700, color: '#6c757d' }}>
                        {label}
                    </div>
                    <OverlayTrigger
                        trigger="click"
                        placement="top"
                        rootClose
                        overlay={(
                            <Popover id={helperId}>
                                <Popover.Body>{helper}</Popover.Body>
                            </Popover>
                        )}
                    >
                        <button
                            type="button"
                            aria-label={`${label} info`}
                            title={`${label} info`}
                            onClick={(e) => e.stopPropagation()}
                            style={{
                                width: '18px',
                                height: '18px',
                                borderRadius: '50%',
                                border: `1px solid ${accent}`,
                                background: '#fff',
                                color: accent,
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '11px',
                                lineHeight: 1,
                                padding: 0,
                                cursor: 'pointer',
                                flex: '0 0 auto',
                            }}
                        >
                            i
                        </button>
                    </OverlayTrigger>
                </div>
                <div style={{ marginTop: '6px', fontSize: '18px', fontWeight: 800, color: accent }}>
                    {value}
                </div>
            </div>
        );
    };

    renderGoodPartsBreakdownModal = () => {
        const row = this.state.goodPartsBreakdownModal;
        if (!row) return null;

        const rows = row.goodPartsBreakdown || [];
        const movementRows = rows.filter(item => item.sourceType !== 'BALANCE');
        const receivingTotal = movementRows
            .filter(item => item.sourceType === 'RECEIVING')
            .reduce((sum, item) => sum + (Number(item.quantity) || 0), 0);
        const transferOutTotal = movementRows
            .filter(item => item.sourceType === 'TRANSFER' && Number(item.signedQty || 0) < 0)
            .reduce((sum, item) => sum + Math.abs(Number(item.signedQty) || 0), 0);
        const transferInTotal = movementRows
            .filter(item => item.sourceType === 'TRANSFER' && Number(item.signedQty || 0) > 0)
            .reduce((sum, item) => sum + (Number(item.signedQty) || 0), 0);
        const total = Number(row.goodParts || 0);
        const movementNet = receivingTotal + transferInTotal - transferOutTotal;
        const balanceDifference = total - movementNet;

        return (
            <Modal
                show
                onHide={() => this.setState({ goodPartsBreakdownModal: null })}
                size="xl"
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>Good Parts Movement Breakdown</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div style={{ marginBottom: '12px', color: '#495057' }}>
                        <strong>{row.material}</strong>
                        {row.materialDesc ? ` - ${row.materialDesc}` : ''}
                    </div>
                    <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '14px' }}>
                        {this.renderBreakdownSummaryCard({
                            label: 'LIVE BALANCE USED',
                            value: this.formatDetailNumber(total),
                            helper: 'The current usable stock MRP uses for Good Parts. This comes from Inventory Balance for RM Warehouse locations.',
                        })}
                        {this.renderBreakdownSummaryCard({
                            label: 'MOVEMENT NET',
                            value: this.formatDetailNumber(movementNet),
                            helper: 'Receiving plus Transfer In minus Transfer Out from the movement rows shown in this modal.',
                        })}
                        {this.renderBreakdownSummaryCard({
                            label: 'BALANCE DIFFERENCE',
                            value: `${balanceDifference > 0 ? '+' : ''}${this.formatDetailNumber(balanceDifference)}`,
                            color: balanceDifference === 0 ? '#212529' : '#b58100',
                            helper: 'Live Balance Used minus Movement Net. A non-zero value means the live balance includes older/manual adjustments or movements not fully represented by the displayed history.',
                        })}
                        {this.renderBreakdownSummaryCard({
                            label: 'RECEIVED',
                            value: this.formatDetailNumber(receivingTotal),
                            color: '#198754',
                            helper: 'Total accepted receiving quantity for this material in the movement history.',
                        })}
                        {this.renderBreakdownSummaryCard({
                            label: 'TRANSFER IN',
                            value: this.formatDetailNumber(transferInTotal),
                            color: '#198754',
                            helper: 'Confirmed RM transfer quantity that moved this material back into usable RM Warehouse stock.',
                        })}
                        {this.renderBreakdownSummaryCard({
                            label: 'TRANSFER OUT',
                            value: this.formatDetailNumber(transferOutTotal),
                            color: '#dc3545',
                            helper: 'Confirmed RM transfer quantity that moved this material out of usable RM Warehouse stock, usually to Production or Scrap.',
                        })}
                    </div>
                    <div style={{ maxHeight: '420px', overflow: 'auto', border: '1px solid #dee2e6', borderRadius: '6px' }}>
                        <table className="table table-sm table-hover" style={{ marginBottom: 0 }}>
                            <thead style={{ position: 'sticky', top: 0, background: '#f8f9fa', zIndex: 1 }}>
                                <tr>
                                    <th>Date</th>
                                    <th>Movement</th>
                                    <th>Reference</th>
                                    <th>Location Flow</th>
                                    <th>Status</th>
                                    <th>Lot No</th>
                                    <th style={{ textAlign: 'right' }}>Quantity</th>
                                    <th>Created By</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.length === 0 ? (
                                    <tr>
                                        <td colSpan="8" style={{ textAlign: 'center', color: '#6c757d', padding: '18px' }}>
                                            No inventory movement breakdown available.
                                        </td>
                                    </tr>
                                ) : rows.map((item, index) => (
                                    <tr key={`${item.id || item.transactionId || item.grnNo || index}-${index}`}>
                                        <td>{this.formatBreakdownDate(item.movementDate || item.receivedAt)}</td>
                                        <td>{item.movementType || item.sourceType || '-'}</td>
                                        <td>{item.reference || item.transactionId || item.grnNo || item.invoiceNo || '-'}</td>
                                        <td>{this.formatMovementLocation(item)}</td>
                                        <td>{item.status || item.area || '-'}</td>
                                        <td>{item.lotNo || item.supplierMfgLotNo || '-'}</td>
                                        <td style={{ textAlign: 'right' }}>{this.renderMovementQuantity(item)}</td>
                                        <td>{item.createdBy || '-'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => this.setState({ goodPartsBreakdownModal: null })}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        );
    };

    renderBookedBreakdownModal = () => {
        const row = this.state.bookedBreakdownModal;
        if (!row) return null;

        const rows = row.bookedBreakdown || [];
        const joRows = rows.filter(item => item.sourceType === 'JO');
        const soRows = rows.filter(item => item.sourceType === 'SO');
        const total = Number(row.bookedQty || 0);

        return (
            <Modal
                show
                onHide={() => this.setState({ bookedBreakdownModal: null })}
                size="xl"
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>Booked Qty Breakdown</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div style={{ marginBottom: '12px', color: '#495057' }}>
                        <strong>{row.material}</strong>
                        {row.materialDesc ? ` - ${row.materialDesc}` : ''}
                    </div>
                    <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '14px' }}>
                        <div style={{ padding: '8px 10px', border: '1px solid #dee2e6', borderRadius: '6px', minWidth: '130px' }}>
                            <div style={{ fontSize: '11px', fontWeight: 700, color: '#6c757d' }}>TOTAL BOOKED</div>
                            <div style={{ fontSize: '18px', fontWeight: 800 }}>{this.formatDetailNumber(total)}</div>
                        </div>
                        <div style={{ padding: '8px 10px', border: '1px solid #dee2e6', borderRadius: '6px', minWidth: '130px' }}>
                            <div style={{ fontSize: '11px', fontWeight: 700, color: '#6c757d' }}>OPEN JO</div>
                            <div style={{ fontSize: '18px', fontWeight: 800 }}>{this.formatDetailNumber(joRows.reduce((sum, item) => sum + (Number(item.bookedQty) || 0), 0))}</div>
                        </div>
                        <div style={{ padding: '8px 10px', border: '1px solid #dee2e6', borderRadius: '6px', minWidth: '130px' }}>
                            <div style={{ fontSize: '11px', fontWeight: 700, color: '#6c757d' }}>APPROVED SO</div>
                            <div style={{ fontSize: '18px', fontWeight: 800 }}>{this.formatDetailNumber(soRows.reduce((sum, item) => sum + (Number(item.bookedQty) || 0), 0))}</div>
                        </div>
                    </div>
                    <div style={{ maxHeight: '420px', overflow: 'auto', border: '1px solid #dee2e6', borderRadius: '6px' }}>
                        <table className="table table-sm table-hover" style={{ marginBottom: 0 }}>
                            <thead style={{ position: 'sticky', top: 0, background: '#f8f9fa', zIndex: 1 }}>
                                <tr>
                                    <th>Source</th>
                                    <th>Reference</th>
                                    <th>FG Part No</th>
                                    <th>FG Description</th>
                                    <th style={{ textAlign: 'right' }}>Planned Output</th>
                                    <th style={{ textAlign: 'right' }}>Actual Output</th>
                                    <th style={{ textAlign: 'right' }}>Usage</th>
                                    <th style={{ textAlign: 'right' }}>Booked Qty</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.length === 0 ? (
                                    <tr>
                                        <td colSpan="8" style={{ textAlign: 'center', color: '#6c757d', padding: '18px' }}>
                                            No breakdown available.
                                        </td>
                                    </tr>
                                ) : rows.map((item, index) => (
                                    <tr key={`${item.sourceType}-${item.jobNo || item.soId || index}-${index}`}>
                                        <td>{item.sourceType === 'SO' ? 'Approved SO' : 'Open JO'}</td>
                                        <td>{item.sourceType === 'SO' ? (item.soId || '-') : (item.jobNo || '-')}</td>
                                        <td>{item.fgPartNo || '-'}</td>
                                        <td>{item.fgPartDesc || '-'}</td>
                                        <td style={{ textAlign: 'right' }}>{item.sourceType === 'SO' ? this.formatDetailNumber(item.salesOrderQty) : this.formatDetailNumber(item.plannedQty)}</td>
                                        <td style={{ textAlign: 'right' }}>{item.sourceType === 'SO' ? '-' : this.formatDetailNumber(item.actualQty)}</td>
                                        <td style={{ textAlign: 'right' }}>{this.formatDetailNumber(item.usage)}</td>
                                        <td style={{ textAlign: 'right', fontWeight: 700 }}>{this.formatDetailNumber(item.bookedQty)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => this.setState({ bookedBreakdownModal: null })}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        );
    };

    renderKpiTile = ({ label, value, tone, helper }) => {
        const tones = {
            blue: { border: '#b6d4fe', bg: '#f1f7ff', accent: '#0d6efd' },
            cyan: { border: '#9eeaf9', bg: '#effcff', accent: '#087990' },
            green: { border: '#a3cfbb', bg: '#f0fbf5', accent: '#198754' },
            amber: { border: '#ffe69c', bg: '#fff9e8', accent: '#b58100' },
            red: { border: '#f1aeb5', bg: '#fff5f6', accent: '#dc3545' },
            dark: { border: '#ced4da', bg: '#f8f9fa', accent: '#343a40' },
            slate: { border: '#cfd4da', bg: '#f6f8fa', accent: '#495057' },
        };
        const color = tones[tone] || tones.slate;

        return (
            <div
                key={label}
                style={{
                    minWidth: 0,
                    padding: '10px 12px',
                    border: `1px solid ${color.border}`,
                    borderLeft: `4px solid ${color.accent}`,
                    borderRadius: '6px',
                    backgroundColor: color.bg,
                }}
            >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                    <div style={{ fontSize: '11px', fontWeight: 700, color: '#6c757d', textTransform: 'uppercase', minWidth: 0 }}>
                        {label}
                    </div>
                    {helper && (
                        <OverlayTrigger
                            trigger="click"
                            placement="top"
                            rootClose
                            overlay={(
                                <Popover id={`mrp-kpi-helper-${label.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}>
                                    <Popover.Body>{helper}</Popover.Body>
                                </Popover>
                            )}
                        >
                            <button
                                type="button"
                                aria-label={`${label} info`}
                                title={`${label} info`}
                                onClick={(e) => e.stopPropagation()}
                                style={{
                                    width: '18px',
                                    height: '18px',
                                    borderRadius: '50%',
                                    border: `1px solid ${color.accent}`,
                                    background: '#fff',
                                    color: color.accent,
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '11px',
                                    lineHeight: 1,
                                    padding: 0,
                                    cursor: 'pointer',
                                    flex: '0 0 auto',
                                }}
                            >
                                i
                            </button>
                        </OverlayTrigger>
                    )}
                </div>
                <div style={{ marginTop: '4px', fontSize: '15px', fontWeight: 800, lineHeight: 1.1, color: color.accent }}>
                    {this.formatSummaryNumber(value)}
                </div>
            </div>
        );
    };

    renderDemandOverview = () => {
        const { summary, bomList, shortages, fgDemand, loading, searched } = this.state;
        const metrics = buildMrpKpiMetrics({ summary, bomList, shortages, fgDemand });

        return (
            <div
                style={{
                    marginBottom: '20px',
                    marginTop: '20px',
                    padding: '16px',
                    backgroundColor: '#fff',
                    border: '1px solid #dee2e6',
                    borderRadius: '6px',
                    boxShadow: '0 1px 2px rgba(16, 24, 40, 0.04)',
                }}
            >
                <Row className="align-items-center" style={{ rowGap: '12px' }}>
                    <Col sm={12} md={8}>
                        <div style={{ fontSize: '12px', fontWeight: 700, color: '#6c757d', textTransform: 'uppercase' }}>
                            Demand Source
                        </div>
                        <div style={{ marginTop: '3px', fontSize: '16px', fontWeight: 700, color: '#212529' }}>
                            Approved Sales Orders
                        </div>
                        <div style={{ marginTop: '2px', fontSize: '13px', color: '#6c757d' }}>
                            Current company demand, auto-loaded against BOM material availability
                        </div>
                    </Col>
                    <Col sm={12} md={4} style={{ textAlign: 'right' }}>
                        <Button
                            variant="primary"
                            id="mrp-refresh-btn"
                            onClick={this.loadMrp}
                            disabled={loading}
                        >
                            {loading ? 'Loading...' : 'Refresh'}
                        </Button>
                        <div style={{ marginTop: '6px', fontSize: '12px', color: '#6c757d' }}>
                            {searched ? 'Latest approved SO snapshot' : 'Ready to load approved SO demand'}
                        </div>
                    </Col>
                </Row>

                {searched && !loading && (
                    <div
                        style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                            gap: '10px',
                            marginTop: '14px',
                        }}
                    >
                        {metrics.map(metric => this.renderKpiTile(metric))}
                    </div>
                )}
            </div>
        );
    };

    render() {
        try {
            const userData = localStorage.getItem('user');
            if (!userData) return <Navigate to="/login" replace />;
            const user = JSON.parse(userData);
            if (!canAccessPlanningModule(user)) return <Navigate to="/" replace />;
        } catch (e) {
            return <Navigate to="/login" replace />;
        }

        if (this.state.purchaseOrderDraft) {
            return (
                <Navigate
                    to="/purchases/purchase-orders/add"
                    state={{ purchaseOrderDraft: this.state.purchaseOrderDraft }}
                />
            );
        }

        const { bomList, shortages, loading, searched, error } = this.state;

        return (
            <div className="container">
                <div style={{ marginTop: '20px' }}>
                    <h4>Material Requirements Planning (MRP)</h4>
                    <p className="text-muted">Automatically checks approved Sales Orders against BOM material availability</p>

                    {this.renderDemandOverview()}

                    {error && (
                        <div style={{ marginBottom: '20px', padding: '10px 12px', border: '1px solid #f1aeb5', borderRadius: '6px', backgroundColor: '#fff5f6', color: '#b02a37', fontSize: '14px' }}>
                            {error}
                        </div>
                    )}

                    {searched && bomList.length > 0 && (
                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '10px' }}>
                            <Button
                                variant="primary"
                                size="sm"
                                onClick={() => utils.tableToExcel('mrp-export-table', 'MRP Details', 'MRP_Details_' + moment().format('YYYYMMDD') + '.xls')}
                            >
                                Export MRP
                            </Button>
                        </div>
                    )}

                    <div id="mrp-datatable">
                        <DataTable
                            key={`mrp-details-${this.state.compactListVersion}`}
                            columns={this.columns}
                            data={bomList}
                            progressPending={loading}
                            progressComponent={<DatacoLoadingIndicator loading={loading} size="compact" label="Loading material requirements" />}
                            pagination
                            paginationPerPage={30}
                            paginationRowsPerPageOptions={[10, 20, 30, 50, 100]}
                            highlightOnHover
                            striped
                            responsive
                            noDataComponent={
                                searched
                                    ? 'No material requirements found for approved Sales Orders.'
                                    : 'MRP data loads automatically from approved Sales Orders.'
                            }
                        />
                    </div>

                    {searched && !loading && shortages.length > 0 && (
                        <div style={{ marginTop: '40px', marginBottom: '40px', width: '100%' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                                <h5 style={{ fontWeight: '700', color: '#333', margin: 0 }}>MRP Summary Report (Negative Variance)</h5>
                                <Button
                                    variant="primary"
                                    size="sm"
                                    onClick={() => utils.tableToExcel('mrp-summary-export-table', 'MRP Summary', 'MRP_Summary_' + moment().format('YYYYMMDD') + '.xls')}
                                >
                                    Export Summary
                                </Button>
                            </div>
                            <div id="mrp-summary-datatable">
                                <DataTable
                                    key={`mrp-summary-${this.state.compactListVersion}`}
                                    columns={this.summaryColumns}
                                    data={shortages}
                                    highlightOnHover
                                    striped
                                    responsive
                                    noDataComponent="No negative variance materials found."
                                />
                            </div>
                        </div>
                    )}

                    <div id="mrp-export-table" style={{ display: 'none' }}>
                        <table border="1" style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr>
                                    <th>No</th>
                                    <th>Material</th>
                                    <th>Material Description</th>
                                    <th>FG Parts</th>
                                    <th>SO Ref</th>
                                    <th>UoM</th>
                                    <th>Where Use</th>
                                    <th>Source</th>
                                    <th>Supplier</th>
                                    <th>Good Parts</th>
                                    <th>On Hold</th>
                                    <th>Booked Qty</th>
                                    <th>Net Inventory</th>
                                    <th>Available Quantity</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(bomList || []).map((item, index) => (
                                    <tr key={item.id || item.material || index}>
                                        <td>{index + 1}</td>
                                        <td>{item.material || ''}</td>
                                        <td>{item.materialDesc || ''}</td>
                                        <td>{(item.fgParts || []).join(', ')}</td>
                                        <td>{(item.soRefs || []).join(', ')}</td>
                                        <td>{item.uom || ''}</td>
                                        <td>{item.whereUse || ''}</td>
                                        <td>{item.source || ''}</td>
                                        <td>{item.supplier || ''}</td>
                                        <td>{item.goodParts || 0}</td>
                                        <td>{item.onHold || 0}</td>
                                        <td>{item.bookedQty || 0}</td>
                                        <td>{item.totalGoodInventory || 0}</td>
                                        <td>{item.availableQty || 0}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div id="mrp-summary-export-table" style={{ display: 'none' }}>
                        <table border="1" style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr>
                                    <th>Material</th>
                                    <th>Material Description</th>
                                    <th>Shortage Qty</th>
                                    <th>Supplier</th>
                                    <th>SO Ref</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(shortages || []).map((item, index) => (
                                    <tr key={item.id || item.material || index}>
                                        <td>{item.material || ''}</td>
                                        <td>{item.materialDesc || ''}</td>
                                        <td>{Math.abs(Number(item.availableQty) || 0)}</td>
                                        <td>{item.supplier || ''}</td>
                                        <td>{(item.soRefs || []).join(', ')}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {this.renderGoodPartsBreakdownModal()}
                    {this.renderBookedBreakdownModal()}
                </div>
            </div>
        );
    }
}

export { MRP, buildMrpKpiMetrics };
